<script setup lang="ts">
import { RouterLink } from "vue-router";

import BrandLogo from "./BrandLogo.vue";
import { useTheme } from "../composables/useTheme";

const { theme, toggleTheme } = useTheme();

const currentYear = new Date().getFullYear();

const legalLinks = [
  {
    label: "Impressum",
    to: "/impressum",
  },
  {
    label: "AGB",
    to: "/agb",
  },
  {
    label: "Datenschutz",
    to: "/datenschutz",
  },
  {
    label: "Stornierung",
    to: "/stornierung",
  },
];

const socialLinks = [
  {
    href: "https://www.instagram.com/schnittpunkt_lehrte/",
    icon: "simple-icons:instagram",
    label: "Instagram",
  },
  {
    href: "https://www.tiktok.com/@schnittpunktlehrte",
    icon: "simple-icons:tiktok",
    label: "TikTok",
  },
];
</script>

<template>
  <footer class="mt-24 border-t border-app-border bg-app-shell/60 backdrop-blur-md">
    <div class="mx-auto max-w-6xl px-6 py-10">
      <div class="flex flex-col gap-8 md:flex-row md:items-start md:justify-between">
        <div>
          <BrandLogo class="h-16 w-auto shrink-0 md:h-20" />
          <p class="mt-3 text-sm text-app-text-muted">
            Dein Friseur in Lehrte.
          </p>
        </div>

        <div class="text-sm text-app-text-muted md:text-right">
          <address class="flex flex-wrap items-center gap-x-1.5 gap-y-1 not-italic md:justify-end">
            <span>Schnittpunkt Lehrte</span>
            <span class="text-app-text-subtle">·</span>
            <span>Friedrichstr. 18</span>
            <span class="text-app-text-subtle">·</span>
            <span>31275 Lehrte</span>
          </address>
          <div class="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 md:justify-end">
            <a class="inline-flex items-center gap-1.5 transition-colors hover:text-app-text"
              href="tel:+4951328857436">
              <Icon icon="heroicons:phone" class="h-4 w-4" />
              05132 8857436
            </a>
            <span class="text-app-text-subtle">·</span>
            <a class="inline-flex items-center gap-1.5 transition-colors hover:text-app-text"
              href="mailto:info@schnittpunktlehrte.de">
              <Icon icon="heroicons:envelope" class="h-4 w-4" />
              info@schnittpunktlehrte.de
            </a>
          </div>
          <div class="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 md:justify-end">
            <span>Montag bis Samstag: 09:00 – 18:00 Uhr</span>
            <span class="text-app-text-subtle">·</span>
            <span>Sonntag: Ruhetag</span>
          </div>
          <div class="mt-3 flex items-center gap-3 md:justify-end">
            <a v-for="link in socialLinks" :key="link.href"
              class="text-app-text-muted transition-colors hover:text-app-text" :href="link.href"
              rel="noopener noreferrer" target="_blank" :aria-label="link.label">
              <Icon :icon="link.icon" class="h-5 w-5" />
            </a>
          </div>
        </div>
      </div>

      <div class="mt-10 flex flex-col items-center justify-between gap-4 border-t border-app-border pt-6 text-xs text-app-text-subtle sm:flex-row">
        <p class="order-2 sm:order-1">
          © {{ currentYear }} Schnittpunkt Lehrte
        </p>
        <nav class="order-1 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 sm:order-2">
          <template v-for="(link, index) in legalLinks" :key="link.to">
            <RouterLink class="text-app-text-muted transition-colors hover:text-app-text" :to="link.to"
              target="_blank" rel="noopener noreferrer">
              {{ link.label }}
            </RouterLink>
            <span v-if="index < legalLinks.length - 1" class="hidden text-app-text-subtle sm:inline">·</span>
          </template>
        </nav>
        <button type="button"
          class="order-3 inline-flex h-10 w-10 items-center justify-center rounded-full border border-app-border text-app-text-muted transition hover:bg-app-surface hover:text-app-text"
          :aria-label="theme === 'dark' ? 'Helles Design aktivieren' : 'Dunkles Design aktivieren'"
          @click="toggleTheme">
          <Icon :icon="theme === 'dark' ? 'heroicons:sun' : 'heroicons:moon'" class="h-5 w-5" />
        </button>
      </div>
    </div>
  </footer>
</template>
